import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { IconButton } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import "./AIAlerts.scss";
import AIAlertsBrandWhiteLogo from "../../assets/images/ai-alerts/black-logo.png";
import AIAlertBrandDarkLogo from "../../assets/images/ai-alerts/white-logo.png";
import TALogoWhiteIcon from "../../assets/images/ai-alerts/footer-logo-black.png";
import TALogoBlackIcon from "../../assets/images/ai-alerts/footer-logo-white.png";
import { AppStoreIcon, GooglePlayIcon } from "../common/Icon/AIAlerts";
import { connect } from "react-redux";
import ReusableTabs from "../common/Tabs/ReusableTabs";
import LiveAlerts from "./LiveAlerts";
import PastAlerts from "./PastAlerts";
import AlertDetail from "./AlertDetail/AlertDetail";
import { useAuth } from "../../contexts/Auth";
import BeautifulSkeleton from "../common/Skeletons/BeautifulSkeleton";
import WSTelegramCTA from "../WealthSeriesOverview/components/WSTelegramCTA/WSTelegramCTA";

const AIAlerts = ({ themeMode }) => {
  const history = useHistory();
  const { tab, contractId: contractIdFromUrl } = useParams();
  const [showFooterBanner, setShowFooterBanner] = useState(true);
  const [latestActivityDate, setLatestActivityDate] = useState("");
  const { hasAiOptionsAlertsNeural, loading } = useAuth();
  // Check if we're on a detail route
  const isDetailRoute = !!contractIdFromUrl;

  // Determine initial tab based on URL param, default to "live"
  const getTabFromParam = (tabParam) => {
    if (tabParam === "past") return "past";
    return "live";
  };

  const [activeTab, setActiveTab] = useState(getTabFromParam(tab));

  // Sync activeTab with URL param changes
  useEffect(() => {
    // If we're on a detail route (contractIdFromUrl exists), don't update tab
    if (isDetailRoute) {
      return;
    }

    const urlTab = getTabFromParam(tab);
    setActiveTab(urlTab);
    // Save current tab when viewing tabs (not detail view)
    localStorage.setItem("aiAlertsLastTab", urlTab);
  }, [tab, isDetailRoute]);

  const handleUpgradeClick = () => {
    history.push("/ai-options-alerts-signup");
  };

  const handleCloseFooterBanner = () => {
    setShowFooterBanner(false);
  };

  const handleShowDetails = (contractId) => {
    // Save current tab before navigating to detail
    localStorage.setItem("aiAlertsLastTab", activeTab);
    // Navigate to detail route
    history.push(`/ai-options-alerts/trade/${contractId}`);
  };

  const handleBackFromDetails = () => {
    // Navigate back to the appropriate tab
    const savedTab = localStorage.getItem("aiAlertsLastTab") || "live";
    const tabPath =
      savedTab === "past"
        ? "/ai-options-alerts/past"
        : "/ai-options-alerts/live";
    history.push(tabPath);
  };

  const tabs = [
    { label: "Live Alerts", value: "live", path: "/ai-options-alerts/live" },
    { label: "Past Alerts", value: "past", path: "/ai-options-alerts/past" },
  ];

  const handleTabChange = (e, newValue) => {
    setActiveTab(newValue);
    // Clear latest activity date when switching tabs
    setLatestActivityDate("");
    // Navigate to the corresponding URL
    const selectedTab = tabs.find((t) => t.value === newValue);
    if (selectedTab && selectedTab.path) {
      history.push(selectedTab.path);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "live":
        return (
          <LiveAlerts
            themeMode={themeMode}
            onShowDetails={handleShowDetails}
            onLatestActivityDateChange={setLatestActivityDate}
          />
        );
      case "past":
        return (
          <PastAlerts
            themeMode={themeMode}
            onShowDetails={handleShowDetails}
            onLatestActivityDateChange={setLatestActivityDate}
          />
        );
      default:
        return (
          <LiveAlerts
            themeMode={themeMode}
            onShowDetails={handleShowDetails}
            onLatestActivityDateChange={setLatestActivityDate}
          />
        );
    }
  };

  // If on detail route, render only AlertDetail without header/tabs/footer
  if (isDetailRoute) {
    return (
      <div className="ai-alerts-container">
        <AlertDetail
          themeMode={themeMode}
          contractId={contractIdFromUrl}
          onBack={handleBackFromDetails}
        />
      </div>
    );
  }

  return (
    <div className="ai-alerts-container">
      {/* Header Section */}
      <div className="ai-alerts-header-section">
        <div className="ai-alerts-title-row">
          <div className="ai-alerts-title-left">
            <div className="ai-alerts-icon-wrapper">
              <img
                src={
                  themeMode === "dark"
                    ? AIAlertBrandDarkLogo
                    : AIAlertsBrandWhiteLogo
                }
                alt="AI Options Alerts"
                className="ai-alerts-icon"
              />
            </div>
            <h1 className="ai-alerts-title">AI Options Alerts</h1>
            <div
              className={
                hasAiOptionsAlertsNeural
                  ? "ai-alerts-tier-badge ai-alerts-tier-badge-neural"
                  : "ai-alerts-tier-badge"
              }
            >
              <span
                className={
                  hasAiOptionsAlertsNeural
                    ? "tier-badge-text-nueral"
                    : "tier-badge-text"
                }
              >
                {hasAiOptionsAlertsNeural ? "NEURAL" : "FREE"}
              </span>
            </div>
          </div>
          <WSTelegramCTA
            className="ai-alerts-telegram-button"
            link="https://t.me/+aJZ2hue5ZhM1NzUx"
          />
        </div>
        {loading ? (
          <div className="ai-alerts-subtitle">
            <BeautifulSkeleton width={350} height={20} />
          </div>
        ) : (
          !hasAiOptionsAlertsNeural && (
            <div className="ai-alerts-subtitle">
              <span className="subtitle-normal">You're on the free plan. </span>
              <span className="subtitle-link ml-4" onClick={handleUpgradeClick}>
                {" "}
                Upgrade to Neural for more alerts.
              </span>
            </div>
          )
        )}
      </div>

      {/* Tabs Section */}
      <ReusableTabs
        className="ai-alerts-tabs"
        tabs={tabs}
        value={activeTab}
        onChange={handleTabChange}
        darkMode={themeMode === "dark"}
        endText={latestActivityDate}
      />

      {/* Content Section */}
      <div className="ai-alerts-content-section">{renderTabContent()}</div>

      {/* Footer Section - App Download Banner */}
      {showFooterBanner && (
        <div className="ai-alerts-footer-section">
          <div className="footer-banner">
            <div className="footer-banner-left">
              <IconButton className="footer-app-icon-button">
                <img
                  src={themeMode === "dark" ? TALogoBlackIcon : TALogoWhiteIcon}
                  alt="TradeAlgo"
                  className="footer-ta-icon"
                />
              </IconButton>
              <p className="footer-banner-text">
                Download TradeAlgo on your phone now to receive alerts right in
                your palm
              </p>
            </div>

            <div className="footer-banner-right">
              <div className="footer-badge-button">
                <a
                  href="https://apps.apple.com/sg/app/tradealgo-ai/id6741116698"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="badge-link"
                >
                  <AppStoreIcon />
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.tafintech.tradealgo.app&hl=en&pli=1"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="badge-link"
                >
                  <GooglePlayIcon />
                </a>
              </div>
              <IconButton
                className="footer-close-button"
                onClick={handleCloseFooterBanner}
              >
                <CloseIcon />
              </IconButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const stateToProps = (state) => ({
  themeMode: state.themeMode.themeMode,
});

export default connect(stateToProps)(AIAlerts);
